import { TimeSlotVote } from '../interfaces/interface';

export interface AttendeeVoteInput {
  name: string;
  login: string;
  eaLogin?: string;
  eaName?: string;
}

export interface ProxyInfo {
  login: string;
  name: string;
  eaLogin?: string;
}

export interface ResolvedVote {
  name: string;
  vote: 'up' | 'down' | 'none';
  via?: string;
}

/**
 * Resolves the vote for a single attendee on a single slot.
 *
 * Resolution priority (first match wins):
 *   1. Leader's own direct vote (takes precedence even if EA also voted)
 *   2. Explicit onBehalfOfLogin vote (EA clicked "vote on behalf" — post-May 2026)
 *   3. Legacy: Principal's EA voted as themselves (pre-May 2026 data)
 *   4. Proxy coverage:
 *      4a. Proxy voted as themselves (no onBehalfOfLogin)
 *      4b. Proxy's EA voted on behalf of proxy (onBehalfOfLogin targets proxy)
 *      4c. Proxy's EA voted as themselves (legacy)
 *   5. No vote found → vote: "none"
 */
export function resolveAttendeeVote(
  attendee: AttendeeVoteInput,
  votes: TimeSlotVote[],
  proxy: ProxyInfo | undefined,
  loginToAttendee: Map<string, { name: string; eaName?: string }>
): ResolvedVote {
  // Priority 1: Leader voted directly
  const directVote = votes.find((v) => v.loginName.toLowerCase() === attendee.login.toLowerCase() && !v.onBehalfOfLogin);
  if (directVote) {
    return { name: attendee.name, vote: directVote.vote };
  }

  // Priority 2: Explicit on-behalf vote targeting this principal
  const explicitVote = votes.find((v) => v.onBehalfOfLogin?.toLowerCase() === attendee.login.toLowerCase());
  if (explicitVote) {
    const voterName = attendee.eaName || explicitVote.loginName;
    return { name: attendee.name, vote: explicitVote.vote, via: `EA ${voterName}` };
  }

  // Priority 3: Legacy — Principal's EA voted as themselves
  if (attendee.eaLogin) {
    const legacyEaVote = votes.find((v) => v.loginName.toLowerCase() === attendee.eaLogin!.toLowerCase() && !v.onBehalfOfLogin);
    if (legacyEaVote) {
      const voterName = attendee.eaName || attendee.eaLogin;
      return { name: attendee.name, vote: legacyEaVote.vote, via: `EA ${voterName}` };
    }
  }

  // Priority 4a: Proxy voted as themselves
  if (proxy) {
    const proxyDirectVote = votes.find((v) => v.loginName.toLowerCase() === proxy.login.toLowerCase() && !v.onBehalfOfLogin);
    if (proxyDirectVote) {
      return { name: attendee.name, vote: proxyDirectVote.vote, via: `Proxy ${proxy.name}` };
    }
  }

  // Priority 4b: Proxy's EA voted on behalf of proxy
  if (proxy) {
    const proxyOnBehalfVote = votes.find((v) => v.onBehalfOfLogin?.toLowerCase() === proxy.login.toLowerCase());
    if (proxyOnBehalfVote) {
      return { name: attendee.name, vote: proxyOnBehalfVote.vote, via: `Proxy ${proxy.name}` };
    }
  }

  // Priority 4c: Proxy's EA voted as themselves (legacy)
  if (proxy?.eaLogin) {
    const legacyProxyEaVote = votes.find((v) => v.loginName.toLowerCase() === proxy.eaLogin!.toLowerCase() && !v.onBehalfOfLogin);
    if (legacyProxyEaVote) {
      const voterInfo = loginToAttendee.get(proxy.eaLogin.toLowerCase());
      const voterName = voterInfo?.name || proxy.eaLogin;
      return { name: attendee.name, vote: legacyProxyEaVote.vote, via: `${proxy.name}'s EA ${voterName}` };
    }
  }

  // Priority 5: No vote found
  return { name: attendee.name, vote: 'none' };
}
